// Univerzální našeptávač (command palette) do horní lišty — fulltext napříč celým menu
// + injektovatelné „speciální" výsledky (např. „Otevřít obchodní případ č. X"). Plně
// ovladatelný klávesnicí: ↑/↓ pohyb, Enter = nahradí plochu, Ctrl+Enter = samostatné okno, Esc.
// Položky vypadají stejně jako v menu (reuse MenuRow + MenuConfigContext) vč. breadcrumb cesty.

import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';
import { Input } from '@/shared/ui/input';

import { MENU_DENSITY } from './density';
import { MenuConfigContext, type MenuConfig } from './menuConfigContext';
import { MenuRow } from './MenuRow';
import { menuTypeIcon } from './menuTypeIcon';
import { flattenMenuForSearch } from './menuTreeUtils';
import type {
  MenuActionContext,
  MenuDensity,
  MenuIconName,
  MenuIconResolver,
  MenuItemNode,
  MenuSection,
} from './menuTypes';

/** Akce/cíl otevření výsledku. */
export type MenuSearchMode = 'replace' | 'detached';

/** Injektovaný „speciální" výsledek mimo menu (app dodá konkrétní akci, např. otevření OP dle čísla). */
export interface MenuSearchSpecial {
  readonly id: string;
  readonly title: string;
  /** Ikona řádku; default decentní tečka. */
  readonly icon?: MenuIconName;
  readonly onActivate: (mode: MenuSearchMode) => void;
}

export interface MenuSearchProps {
  /** Celé menu k prohledání (legacy + native sekce). */
  readonly sections: readonly MenuSection[];
  /** Otevření nalezené agendy — `replace` = nahradí plochu, `detached` = samostatné okno. */
  readonly onOpen: (ctx: MenuActionContext, mode: MenuSearchMode) => void;
  /** Hustota řádků. Default `compact`. */
  readonly density?: MenuDensity;
  /** Resolver type-ikony. Default `menuTypeIcon`. */
  readonly iconForNode?: MenuIconResolver;
  /** Vrací top „speciální" výsledky pro daný dotaz (prepend nad menu-shody). */
  readonly specialResults?: (query: string) => readonly MenuSearchSpecial[];
  readonly placeholder?: string;
  readonly className?: string;
}

// Ikonu speciální položky protlačíme přes synteticky `key` (resolver nevidí id řádku).
const SPECIAL_ICON_PREFIX = '$icon:';

interface SearchRow {
  readonly node: MenuItemNode;
  readonly trail?: readonly string[];
  readonly activate: (mode: MenuSearchMode) => void;
}

/** Imperativní handle pro vnější ovládání (globální klávesová zkratka → focus). */
export interface MenuSearchHandle {
  /** Dá focus do inputu a označí stávající text. */
  readonly focus: () => void;
}

export const MenuSearch = forwardRef<MenuSearchHandle, MenuSearchProps>(function MenuSearch(
  {
    sections,
    onOpen,
    density = 'compact',
    iconForNode = menuTypeIcon,
    specialResults,
    placeholder,
    className,
  },
  ref
) {
  const { t } = useTranslation('sharedUi');
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        inputRef.current?.focus();
        inputRef.current?.select();
      },
    }),
    []
  );

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const isOpen = open && query.trim() !== '';

  const rows = useMemo<SearchRow[]>(() => {
    const list: SearchRow[] = [];
    for (const s of specialResults?.(query) ?? []) {
      const node: MenuItemNode = {
        t: 'item',
        id: s.id,
        title: s.title,
        key: s.icon ? `${SPECIAL_ICON_PREFIX}${s.icon}` : undefined,
        origin: 'native',
      };
      list.push({ node, activate: (mode) => s.onActivate(mode) });
    }
    for (const hit of flattenMenuForSearch(sections, query)) {
      list.push({
        node: hit.node,
        trail: hit.trail,
        activate: (mode) => onOpen({ node: hit.node, section: hit.section }, mode),
      });
    }
    return list;
  }, [specialResults, query, sections, onOpen]);

  // Reset aktivní položky při změně dotazu/výsledků (Enter bez výběru → první).
  useLayoutEffect(() => {
    setActiveIdx(0);
  }, [query]);

  // Pozice dropdownu — kotvíme k inputu, pravým okrajem k pravému okraji inputu, clamp do viewportu.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const update = () => {
      const a = anchorRef.current?.getBoundingClientRect();
      if (!a) return;
      const width = Math.min(380, Math.max(280, window.innerWidth - 12));
      let left = a.right - width;
      if (left < 6) left = 6;
      if (left + width > window.innerWidth - 6) left = window.innerWidth - 6 - width;
      setPos({ top: a.bottom + 5, left, width });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen]);

  // Aktivní řádek scrollni do view.
  useLayoutEffect(() => {
    if (!isOpen) return;
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`)?.scrollIntoView({
      block: 'nearest',
    });
  }, [activeIdx, isOpen]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  const activate = useCallback(
    (idx: number, mode: MenuSearchMode) => {
      const row = rows[idx];
      if (!row) return;
      row.activate(mode);
      close();
      inputRef.current?.blur();
    },
    [rows, close]
  );

  const handleKey = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
        setActiveIdx((i) => Math.min(rows.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (rows.length > 0) activate(activeIdx, e.ctrlKey || e.metaKey ? 'detached' : 'replace');
      } else if (e.key === 'Escape') {
        e.preventDefault();
        close();
        inputRef.current?.blur();
      }
    },
    [rows.length, activate, activeIdx, close]
  );

  // Konfigurace pro MenuRow — detach čudlík (jen na hover) = otevři jako samostatné okno.
  const config = useMemo<MenuConfig>(() => {
    const iconResolver: MenuIconResolver = (node) =>
      node.key?.startsWith(SPECIAL_ICON_PREFIX)
        ? (node.key.slice(SPECIAL_ICON_PREFIX.length) as MenuIconName)
        : iconForNode(node);
    return {
      density,
      densityTokens: MENU_DENSITY[density],
      separators: 'lines',
      detachAlways: false,
      iconForNode: iconResolver,
      onOpen: () => {},
      onDetach: (node) => {
        const idx = rows.findIndex((r) => r.node === node);
        if (idx >= 0) activate(idx, 'detached');
      },
    };
  }, [density, iconForNode, rows, activate]);

  const style: CSSProperties = {
    top: pos ? pos.top : -9999,
    left: pos ? pos.left : 0,
    width: pos ? pos.width : undefined,
    visibility: pos ? 'visible' : 'hidden',
  };

  return (
    <div ref={anchorRef} className={cn('w-[210px]', className)}>
      <Input
        ref={inputRef}
        size="sm"
        leadingIcon="search"
        placeholder={placeholder ?? t('menuSearch.placeholder')}
        value={query}
        aria-label={t('menuSearch.placeholder')}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(e.target.value.trim() !== '');
        }}
        onFocus={() => {
          if (query.trim() !== '') setOpen(true);
        }}
        onKeyDown={handleKey}
      />

      {isOpen &&
        createPortal(
          <>
            <div
              className="z-phx-overlay-backdrop fixed inset-0"
              onMouseDown={() => setOpen(false)}
            />
            <div
              role="listbox"
              className="bg-phx-surface border-phx-border-2 rounded-phx-md shadow-phx-lg text-phx-text z-phx-overlay fixed overflow-hidden border"
              style={style}
            >
              <div
                ref={listRef}
                className="phx-menu-scroll max-h-[min(60vh,440px)] overflow-y-auto p-1"
              >
                {rows.length === 0 ? (
                  <div className="text-phx-text-muted text-phx-sm px-2 py-3 text-center">
                    {t('menuSearch.empty')}
                  </div>
                ) : (
                  <MenuConfigContext.Provider value={config}>
                    {rows.map((row, i) => (
                      <div key={`${row.node.id}-${i}`} data-idx={i}>
                        <MenuRow
                          node={row.node}
                          trail={row.trail}
                          selected={i === activeIdx}
                          onActivate={() => activate(i, 'replace')}
                        />
                      </div>
                    ))}
                  </MenuConfigContext.Provider>
                )}
              </div>

              <div className="border-phx-border text-phx-text-muted gap-phx-3_5 py-phx-3_5 text-phx-2xs flex items-center border-t px-3 whitespace-nowrap">
                <span>
                  <b className="text-phx-text-2 font-semibold">Enter</b> –{' '}
                  {t('menuSearch.hintReplace')}
                </span>
                <span className="bg-phx-border-2 h-[11px] w-px shrink-0" />
                <span>
                  <b className="text-phx-text-2 font-semibold">Ctrl+Enter</b> –{' '}
                  {t('menuSearch.hintDetach')}
                </span>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
});
